import { useCallback, useRef } from 'react';

import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { useStoreContext } from 'features/store';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { useServiceModeContext } from 'state/service-mode';

import { useStoreAvailabilityFns } from './use-store-availability-fns';

export const useAutoSelectStoreUtils = () => {
  const isManualRestaurantSelectionRef = useRef<boolean>(undefined);

  const { setStore, resetStore } = useStoreContext();
  const { selectServiceMode } = useServiceModeContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();

  const isAutoSelectableStore = useCallback(
    (store: IRestaurant) => isStoreAvailable(store, ServiceMode.TAKEOUT, store.localTime),
    [isStoreAvailable]
  );

  const getFirstAvailableStore = useCallback(
    async (stores: IRestaurant[]) => {
      for (const store of stores) {
        if (isAutoSelectableStore(store)) {
          return store;
        }
      }

      return undefined;
    },
    [isAutoSelectableStore]
  );

  const handleConfirmRestaurant = ({
    confirmed,
    restaurantToConfirm,
    onSelect,
  }: {
    confirmed: boolean;
    restaurantToConfirm: IRestaurant | null;
    onSelect: (selectedRestaurant: IRestaurant | null) => void;
  }) => {
    if (restaurantToConfirm && confirmed) {
      const nextServiceMode = ServiceMode.TAKEOUT;
      if (!isAutoSelectableStore(restaurantToConfirm)) {
        resetStore();
        onSelect(null);
        return;
      }

      setStore({ ...restaurantToConfirm }, nextServiceMode);
      selectServiceMode(nextServiceMode);
      logEvent(CustomEventNames.STORE_AUTO_SELECTED, EventTypes.Other);
    } else {
      isManualRestaurantSelectionRef.current = true;
      resetStore();
    }
    onSelect(null);
  };

  return {
    getFirstAvailableStore,
    isAutoSelectableStore,
    handleConfirmRestaurant,
    isManualRestaurantSelectionRef,
  };
};
